import React, { useState } from "react";
import clientReviewImg1 from "../assets/images/page-titles/5.webp";
import { Link } from "react-router-dom";
import "../assets/css/pricing.css";

function Pricing() {
  const [openAccordion, setOpenAccordion] = useState(null);

  const toggleAccordion = (index) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  const pricingFAQs = [
    {
      title: "What is the Flexible Hours Model?",
      content:
        "Firms commit to a fixed number of hours each month. The resource is shared across multiple clients, and Inscape manages task allocation. Ideal for firms with variable workloads or seasonal peaks.",
    },
    {
      title: "What is the Full-Time Equivalent (FTE) Resource Model?",
      content:
        "A dedicated resource works exclusively for the firm throughout the month. The firm is responsible for keeping the resource fully engaged and managing priorities. Ideal for firms seeking continuity, knowledge retention, and seamless integration with internal workflows.",
    },
    {
      title: "Can I switch between models?",
      content:
        "Yes, we can discuss adjusting your engagement model based on your firm's evolving needs and workload requirements.",
    },
    {
      title: "What services are included year-round?",
      content:
        "Both models support bookkeeping, BAS/IAS lodgements, payroll, tax return preparation, financial reporting, FBT compliance, tax planning, and year-end support. The timing and allocation differ based on your chosen model.",
    },
  ];

  React.useEffect(() => {
    // Apply background images
    document.querySelectorAll(".bg-img")?.forEach((el) => {
      const img = el.querySelector("img");
      if (img) {
        const parent = el.parentElement;
        if (parent) {
          parent.style.backgroundImage = `url(${img.src})`;
          parent.style.backgroundSize = "cover";
          parent.style.backgroundPosition = "center";
          parent.classList.add("bg-img");
          if (el.classList.contains("background-size-auto")) {
            parent.classList.add("background-size-auto");
          }
        }
        el.remove();
      }
    });
  }, []);

  return (
    <>
      <section className="page-title-layout1 page-title-light bg-parallax bg-overlay text-center">
        <div className="bg-img">
          <img src={clientReviewImg1} alt="background" />
        </div>
        <div className="container">
          <div className="row">
            <div className="col-sm-12 col-md-12 col-lg-8 offset-lg-2">
              <h1 className="pagetitle-heading">
                Pricing &amp; Engagement Model
              </h1>
              <p className="pagetitle-subtitle">
                Flexible solutions tailored to your practice's needs
              </p>
              <nav>
                <ol className="breadcrumb justify-content-center mb-0">
                  <li className="breadcrumb-item">
                    <Link to={"/"}>Home</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Pricing
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </section>

      <section className="pricing-section py-5">
        <div className="container">
          {/* Introduction */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="text-center mb-4">
                <h2 className="section-title">
                  Flexible and Dedicated Resourcing for Australian CPA Firms
                </h2>
                <p className="section-description">
                  At Inscape Outsourcing, we understand that accounting
                  practices manage workloads differently. Some firms need
                  flexible support across multiple clients, while others require
                  full-time, dedicated resources integrated with their internal
                  team.
                </p>
              </div>
            </div>
          </div>

          {/* Engagement Models Comparison Table */}
          <div className="row mb-4">
            <div className="col-12">
              <h3 className="section-subtitle">Our Engagement Models</h3>
              <div className="table-responsive">
                <table className="table table-hover pricing-table">
                  <thead>
                    <tr>
                      <th style={{ width: "25%" }}>Feature</th>
                      <th style={{ width: "37.5%" }} className="flexible-col">
                        Flexible Hours Model
                      </th>
                      <th
                        style={{ width: "37.5%", color: "#fff" }}
                        className="fte-col"
                      >
                        FTE Resource Model
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="feature-name">Resource Allocation</td>
                      <td>Assigned based on agreed hours</td>
                      <td>Dedicated to a single firm</td>
                    </tr>
                    <tr>
                      <td className="feature-name">Hours / Commitment</td>
                      <td>Fixed monthly hours, adjustable</td>
                      <td>Full-time equivalent, fixed monthly</td>
                    </tr>
                    <tr>
                      <td className="feature-name">Work Management</td>
                      <td>Inscape manages and prioritises</td>
                      <td>Firm manages and directs</td>
                    </tr>
                    <tr>
                      <td className="feature-name">Best Suited For</td>
                      <td>Variable or seasonal workloads</td>
                      <td>Continuity and embedded support</td>
                    </tr>
                    <tr>
                      <td className="feature-name">Cost Structure</td>
                      <td>Predictable monthly cost</td>
                      <td>Fixed monthly fee with margin</td>
                    </tr>
                    <tr>
                      <td className="feature-name">Responsibility</td>
                      <td>Inscape ensures utilisation</td>
                      <td>Firm manages priorities</td>
                    </tr>
                    <tr>
                      <td className="feature-name">Knowledge Retention</td>
                      <td>Moderate, shared across clients</td>
                      <td className="highlight">High, fully integrated</td>
                    </tr>
                    <tr>
                      <td className="feature-name">Flexibility</td>
                      <td className="highlight">High, scalable</td>
                      <td>Low, requires discussion</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Year-Round Services Table */}
          <div className="row mb-4">
            <div className="col-12">
              <h3 className="section-subtitle">Year-Round Services Overview</h3>
              <div className="table-responsive">
                <table className="table table-hover pricing-table">
                  <thead>
                    <tr>
                      <th style={{ width: "25%" }}>Service</th>
                      <th style={{ width: "37.5%" }} className="flexible-col">
                        Flexible Hours Model
                      </th>
                      <th
                        style={{ width: "37.5%", color: "#fff" }}
                        className="fte-col"
                      >
                        FTE Resource Model
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="feature-name">
                        Bookkeeping & Reconciliations
                      </td>
                      <td>Transactional and catch-up work as needed</td>
                      <td>Continuous support throughout the year</td>
                    </tr>
                    <tr>
                      <td className="feature-name">BAS/IAS Lodgements</td>
                      <td>During lodgement periods, as required</td>
                      <td>Managed across all periods for different clients</td>
                    </tr>
                    <tr>
                      <td className="feature-name">Payroll Support</td>
                      <td>During peak cycles or as needed</td>
                      <td>
                        Continuous including super and leave reconciliations
                      </td>
                    </tr>
                    <tr>
                      <td className="feature-name">Tax Return Preparation</td>
                      <td>During relevant periods for selected clients</td>
                      <td>Throughout the year per lodgement schedules</td>
                    </tr>
                    <tr>
                      <td className="feature-name">Financial Reporting</td>
                      <td>In line with deadlines for selected clients</td>
                      <td>
                        Ongoing for different clients at reporting periods
                      </td>
                    </tr>
                    <tr>
                      <td className="feature-name">FBT Compliance</td>
                      <td>Around FBT year-end or as required</td>
                      <td>Managed at applicable FBT reporting periods</td>
                    </tr>
                    <tr>
                      <td className="feature-name">Tax Planning</td>
                      <td>At key times for selected clients</td>
                      <td>Across the year based on planning needs</td>
                    </tr>
                    <tr>
                      <td className="feature-name">Year-End Support</td>
                      <td>During peak periods</td>
                      <td>Throughout year at respective year-ends</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="alert alert-info mt-3">
                <p className="mb-2">
                  <strong>Flexible Hours Model:</strong> Services delivered per
                  agreed hours. Not all services used for every client.
                </p>
                <p className="mb-0">
                  <strong>FTE Model:</strong> Services delivered throughout the
                  year across different clients per engagement priorities.
                </p>
              </div>
            </div>
          </div>

          {/* How the Models Work */}
          <div className="row mb-4">
            <div className="col-12">
              <h3 className="section-subtitle">How the Models Work</h3>
            </div>
            <div className="col-md-6 mb-3">
              <div className="model-work-card">
                <h5 className="model-work-title flexible-title">
                  <i className="fas fa-clock"></i> Flexible Hours Model
                </h5>
                <ul className="model-work-list">
                  <li>Fixed number of hours each month</li>
                  <li>Resource shared across clients</li>
                  <li>Inscape manages task allocation</li>
                  <li>Ideal for variable or seasonal workloads</li>
                </ul>
              </div>
            </div>
            <div className="col-md-6 mb-3">
              <div className="model-work-card">
                <h5 className="model-work-title fte-title">
                  <i className="fas fa-user-tie"></i> FTE Resource Model
                </h5>
                <ul className="model-work-list">
                  <li>Dedicated resource exclusively for firm</li>
                  <li>Firm manages priorities and tasks</li>
                  <li>Full integration with workflows</li>
                  <li>Ideal for continuity and knowledge retention</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Why Firms Choose Us */}
          <div className="row mb-4">
            <div className="col-12">
              <h3 className="section-subtitle">Why Firms Choose Us</h3>
            </div>
            <div className="col-md-6 col-lg-3 mb-3">
              <div className="benefit-box">
                <i className="fas fa-arrows-alt benefit-icon"></i>
                <h6>Flexible & Scalable</h6>
                <p>Adapts to your practice's needs</p>
              </div>
            </div>
            <div className="col-md-6 col-lg-3 mb-3">
              <div className="benefit-box">
                <i className="fas fa-user-graduate benefit-icon"></i>
                <h6>Experienced Team</h6>
                <p>Australian accounting & tax experts</p>
              </div>
            </div>
            <div className="col-md-6 col-lg-3 mb-3">
              <div className="benefit-box">
                <i className="fas fa-dollar-sign benefit-icon"></i>
                <h6>Transparent Costs</h6>
                <p>Clear terms, no hidden fees</p>
              </div>
            </div>
            <div className="col-md-6 col-lg-3 mb-3">
              <div className="benefit-box">
                <i className="fas fa-lightbulb benefit-icon"></i>
                <h6>Focus on Advisory</h6>
                <p>Reduced admin burden</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Pricing;
